import React, { useId } from 'react'

import { Icon, ChevronRight } from '@cgtrader/triangle-ui/dist/src/ssr-index'
import { string, array } from 'prop-types'

import { useSubcategoryMenu } from 'Pods/UnifiedNavigation/Shared/AssetItem/Hooks/useSubcategoryMenu'
import useListingCategoryNavigation from 'Pods/UnifiedNavigation/Shared/Hooks/useListingCategoryNavigation'

import { EMPTY_ARRAY } from 'Utils/stableDefaults'
import useBreakpoint from 'Utils/useBreakpoint'

import MegaRow from '../MegaRow/MegaRow'
import SubcategoryFlyout from '../SubcategoryFlyout/SubcategoryFlyout'

const DISCLOSURE_CHEVRON_SIZE = { width: '12', height: '12' }

// A single category row plus its subcategory flyout. Owns one useSubcategoryMenu instance
// so each category opens independently. For an expandable category the whole row drills into
// the flyout on mobile (tapping either the label or the arrow), while on desktop the row is a
// navigable link (to the category page, or an in-place listing-filter switch via
// useListingCategoryNavigation when already on a listing) and hover/focus opens the flyout.
// A real disclosure <button> (aria-expanded/aria-controls) sits beside the row: it toggles
// the flyout on mobile and opens it on desktop, and is keyboard-operable on both. Categories
// without subcategories render as a plain link with no disclosure.
function CategoryItem({
  title,
  iconSlug = '',
  href = undefined,
  subcategories = EMPTY_ARRAY,
  browseAllHref = undefined,
  browseAllLabel = undefined,
}) {
  const {
    isSubmenuOpen,
    hoverZoneRef,
    handleToggleSubmenu,
    handleOpenSubmenu,
    handleCloseSubmenu,
    handleHoverZoneMouseEnter,
    handleHoverZoneMouseLeave,
    handleSubmenuFocus,
    handleSubmenuBlur,
  } = useSubcategoryMenu()

  const breakpoint = useBreakpoint()
  const isMobile = ['xs', 'sm'].includes(breakpoint)
  const handleCategoryLinkClick = useListingCategoryNavigation({ isMobile })

  const generatedId = useId()
  const labelId = `${generatedId}-label`
  const flyoutId = `${generatedId}-flyout`
  const hasSubcategories = subcategories.length > 0

  // On mobile, an expandable category's whole row (label + arrow) drills into its subcategory
  // flyout instead of navigating, so the entire row is one large drill-in target. On desktop
  // the row stays a navigable link (hover opens the flyout) and keeps the listing-category
  // bridge. Rows without subcategories always navigate.
  const handleRowClick = (event) => {
    if (isMobile && hasSubcategories) {
      event.preventDefault()
      handleToggleSubmenu()
      return
    }

    handleCategoryLinkClick(event, href)
  }

  // Mobile: the disclosure toggles the flyout (tap to drill in, tap again to collapse).
  // Desktop: hover and focus already open it, and the click fires right after the focus that
  // opened it - toggling here would immediately close it again, so the button only opens.
  const handleDisclosureClick = () => {
    if (isMobile) {
      handleToggleSubmenu()
      return
    }

    handleOpenSubmenu()
  }

  if (!hasSubcategories) {
    return (
      <div className="category-mega-menu__category">
        <MegaRow
          href={href}
          label={title}
          iconSlug={iconSlug}
          onClick={handleRowClick}
        />
      </div>
    )
  }

  return (
    <div
      className="category-mega-menu__category"
      ref={hoverZoneRef}
      onMouseEnter={handleHoverZoneMouseEnter}
      onMouseLeave={handleHoverZoneMouseLeave}
      onFocus={handleSubmenuFocus}
      onBlur={handleSubmenuBlur}
    >
      <MegaRow
        href={href}
        label={title}
        labelId={labelId}
        iconSlug={iconSlug}
        onClick={handleRowClick}
      />
      <button
        type="button"
        className="category-mega-menu__disclosure"
        aria-labelledby={labelId}
        aria-expanded={isSubmenuOpen}
        aria-controls={flyoutId}
        onClick={handleDisclosureClick}
      >
        <Icon
          notSpaced
          color="dark"
          size={DISCLOSURE_CHEVRON_SIZE}
          svgComponent={ChevronRight}
          className="category-mega-menu__disclosure-chevron"
        />
      </button>
      <SubcategoryFlyout
        id={flyoutId}
        subcategories={subcategories}
        categoryTitle={title}
        isOpen={isSubmenuOpen}
        onClose={handleCloseSubmenu}
        onLinkClick={handleCategoryLinkClick}
        browseAllHref={browseAllHref}
        browseAllLabel={browseAllLabel}
      />
    </div>
  )
}

CategoryItem.propTypes = {
  title: string.isRequired,
  iconSlug: string,
  href: string,
  subcategories: array,
  browseAllHref: string,
  browseAllLabel: string,
}

export default CategoryItem
